import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ChatThreadProps {
  /** Read out by screen readers ("Conversation with Ana", "Sage"). */
  label: string;
  children?: ReactNode;
  loading?: boolean;
  /** The messages could not be loaded: says so (never an empty inbox) and offers to try again. */
  error?: { onRetry?: () => void } | null;
  /** Nothing to show yet: this sentence (or node) in the middle. */
  empty?: ReactNode;
  isEmpty?: boolean;
  /** Below the last message: a typing indicator, a "start a new chat" button. */
  footer?: ReactNode;
  /** Changes when there is something new to show (message count, a sending flag). */
  scrollKey?: unknown;
  className?: string;
}

export interface ChatThreadHandle {
  scrollToEnd: () => void;
}

/** How close to the bottom still counts as "reading the latest". */
const NEAR_END_PX = 120;

/**
 * The one message list. Screen readers hear new messages (role="log").
 * It follows new messages only when the reader is already at the bottom, so
 * scrolling back to read is never yanked away. Loading, empty and error
 * states look the same in every chat.
 */
const ChatThread = forwardRef<ChatThreadHandle, ChatThreadProps>(
  ({ label, children, loading, error, empty, isEmpty, footer, scrollKey, className }, ref) => {
    const box = useRef<HTMLDivElement>(null);
    const atEnd = useRef(true);
    const scrollToEnd = useCallback(() => {
      const el = box.current;
      if (el) el.scrollTop = el.scrollHeight;
      atEnd.current = true;
    }, []);
    useImperativeHandle(ref, () => ({ scrollToEnd }), [scrollToEnd]);
    useLayoutEffect(() => {
      if (atEnd.current) scrollToEnd();
    }, [scrollKey, loading, scrollToEnd]);
    // The box gets shorter when the phone keyboard opens (or the URL bar
    // moves): stay on the newest message if that is where the reader was.
    useEffect(() => {
      const el = box.current;
      if (!el || typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(() => {
        if (atEnd.current) el.scrollTop = el.scrollHeight;
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, []);
    const onScroll = () => {
      const el = box.current;
      if (el) atEnd.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_END_PX;
    };
    return (
      <div
        ref={box}
        onScroll={onScroll}
        role="log"
        aria-live="polite"
        aria-label={label}
        // Session replay (LogRocket) never records what people say to each
        // other (Oct 19): data-private elements are redacted.
        data-private=""
        className={cn("flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3", className)}
      >
        {loading ? (
          <div className="flex justify-center py-10 text-muted-foreground" role="status" aria-label="Loading messages">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="py-10 text-center space-y-3">
            <p className="text-sm text-muted-foreground">Couldn't load the messages.</p>
            {error.onRetry && (
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={error.onRetry}>
                Try again
              </Button>
            )}
          </div>
        ) : isEmpty && empty ? (
          typeof empty === "string" ? <p className="text-center text-sm text-muted-foreground py-10">{empty}</p> : empty
        ) : (
          children
        )}
        {!loading && !error && footer}
      </div>
    );
  },
);
ChatThread.displayName = "ChatThread";

export default ChatThread;
